import { getAllAxisGroupNodes } from './axisNodes'

// Extract the y-translate from a `translate(x, y)` transform attribute.
// For x-axis groups this equals chartHeight, giving the exact line length needed
// when no y-axis group exists (e.g. Gantt chart).
function getTranslateY(element) {
  const transform = element?.getAttribute?.('transform') || ''
  const match = transform.match(/translate\s*\(\s*-?[\d.]+\s*,\s*(-?[\d.]+)\s*\)/)
  if (match) {
    const y = parseFloat(match[1])
    if (Number.isFinite(y) && y > 0) return y
  }
  return null
}

function getSiblingAxisGroup(axisGroup, axis) {
  const parent = axisGroup.parentElement
  if (!parent) return null
  const normalizedId = axis === 'y' ? 'yAxis' : 'xAxis'
  const spacedId = axis === 'y' ? 'y axis' : 'x axis'

  // Named sibling (multiset barchart: #xAxis / #yAxis in same panel container)
  const named =
    parent.querySelector(`#${normalizedId}`) ||
    parent.querySelector(`[id="${spacedId}"]`)
  if (named && named !== axisGroup) return named

  // Unnamed sibling inside a #axis container (linechart: two anonymous <g>
  // children of #axis, x-axis last child, y-axis first child)
  if ((parent.getAttribute('id') || '') === 'axis') {
    const children = Array.from(parent.children).filter((c) => c !== axisGroup)
    return children[0] || null
  }

  return null
}

const SVG_NS = 'http://www.w3.org/2000/svg'

function normalizeNumber(value) {
  const nextValue = Number(value)
  return Number.isFinite(nextValue) ? nextValue : 0
}

function isAxisTitleCandidate(node) {
  if (!(node instanceof Element)) return false
  if (node.closest('.tick')) return false

  const className = (node.getAttribute('class') || '').toLowerCase()
  const parentClass = (node.parentElement?.getAttribute('class') || '').toLowerCase()
  const parentId = (node.parentElement?.getAttribute('id') || '').toLowerCase()
  return (
    className.includes('label') ||
    className.includes('title') ||
    parentClass.includes('label') ||
    parentClass.includes('title') ||
    parentId.includes('label') ||
    parentId.includes('title')
  )
}

function getAxisBounds(axisGroup) {
  if (!axisGroup) return null

  try {
    const box = axisGroup.getBBox()
    if (box) {
      return {
        left: box.x,
        right: box.x + box.width,
        top: box.y,
        bottom: box.y + box.height,
        width: box.width,
        height: box.height,
      }
    }

    const childBoxes = Array.from(axisGroup.children || [])
      .filter(
        (node) =>
          node &&
          node.nodeType === 1 &&
          !isAxisTitleCandidate(node)
      )
      .map((node) => {
        try {
          return node.getBBox()
        } catch (error) {
          return null
        }
      })
      .filter(Boolean)

    if (!childBoxes.length) {
      const box = axisGroup.getBBox()
      return {
        left: box.x,
        right: box.x + box.width,
        top: box.y,
        bottom: box.y + box.height,
        width: box.width,
        height: box.height,
      }
    }

    const left = Math.min(...childBoxes.map((box) => box.x))
    const right = Math.max(...childBoxes.map((box) => box.x + box.width))
    const top = Math.min(...childBoxes.map((box) => box.y))
    const bottom = Math.max(...childBoxes.map((box) => box.y + box.height))

    return {
      left,
      right,
      top,
      bottom,
      width: right - left,
      height: bottom - top,
    }
  } catch (error) {
    return null
  }
}

function ensureGuideLine(tickNode) {
  let line = tickNode.querySelector('line[data-lugsail-axis-guide-line="true"]')
  if (!line) {
    line = document.createElementNS(SVG_NS, 'line')
    line.setAttribute('data-lugsail-axis-guide-line', 'true')
    line.setAttribute('pointer-events', 'none')
    tickNode.insertBefore(line, tickNode.firstChild || null)
  }
  return line
}

function setLineAttributes(lineNode, attrs = {}) {
  Object.entries(attrs).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      lineNode.setAttribute(key, String(value))
    }
  })
}

function styleGuideLine(lineNode) {
  lineNode.setAttribute('stroke', 'currentColor')
  lineNode.setAttribute('stroke-opacity', '0.16')
  lineNode.setAttribute('stroke-width', '1')
  lineNode.setAttribute('shape-rendering', 'crispEdges')
  lineNode.style.stroke = 'currentColor'
  lineNode.style.strokeOpacity = '0.16'
  lineNode.style.strokeWidth = '1'
  lineNode.style.shapeRendering = 'crispEdges'
  lineNode.style.pointerEvents = 'none'
}

function normalizeSvgMetrics(svgMetrics, svgNode) {
  if (svgMetrics) return svgMetrics

  const viewBox = svgNode.getAttribute('viewBox')
  if (viewBox) {
    const [, , width, height] = viewBox.split(/\s+/).map(Number.parseFloat)
    return {
      x: 0,
      y: 0,
      width: Number.isFinite(width) ? width : 0,
      height: Number.isFinite(height) ? height : 0,
    }
  }

  return {
    x: 0,
    y: 0,
    width: normalizeNumber(svgNode.getAttribute('width')),
    height: normalizeNumber(svgNode.getAttribute('height')),
  }
}

export function applyAxisGuideLinesToSvg(
  svgNode,
  svgMetrics = null,
  chartId = null,
  options = {}
) {
  if (!svgNode) return

  const showVerticalAxisLines = options.showVerticalAxisLines === true
  const showHorizontalAxisLines = options.showHorizontalAxisLines === true
  if (!showVerticalAxisLines && !showHorizontalAxisLines) return

  const metrics = normalizeSvgMetrics(svgMetrics, svgNode)
  const xAxisGroups = getAllAxisGroupNodes(svgNode, 'x')
  const yAxisGroups = getAllAxisGroupNodes(svgNode, 'y')

  if (showVerticalAxisLines && xAxisGroups.length > 0) {
    xAxisGroups.forEach((xAxisGroup) => {
      // Prefer sibling yAxis in the same panel for per-panel line length
      const yAxisGroup =
        getSiblingAxisGroup(xAxisGroup, 'y') || yAxisGroups[0] || null
      const yAxisBounds = yAxisGroup ? getAxisBounds(yAxisGroup) : null
      // xAxisGroup.translateY == data-area height in standard D3 charts (x-axis sits
      // at translate(0, dataHeight)).  This is more reliable than yAxisBounds.height
      // whose coordinate space varies by chart; yAxisBounds is kept as a fallback.
      const lineLength = Math.max(
        0,
        Math.round(getTranslateY(xAxisGroup) || yAxisBounds?.height || metrics.height * 0.75)
      )

      Array.from(xAxisGroup.querySelectorAll('.tick')).forEach((tickNode) => {
        const lineNode = ensureGuideLine(tickNode)
        setLineAttributes(lineNode, { x1: 0, y1: 0, x2: 0, y2: -lineLength })
        styleGuideLine(lineNode)
      })
    })
  }

  if (showHorizontalAxisLines && yAxisGroups.length > 0) {
    yAxisGroups.forEach((yAxisGroup) => {
      const xAxisGroup =
        getSiblingAxisGroup(yAxisGroup, 'x') || xAxisGroups[0] || null
      const xAxisBounds = xAxisGroup ? getAxisBounds(xAxisGroup) : null
      const lineLength = Math.max(
        0,
        Math.round(xAxisBounds?.width || metrics.width * 0.75)
      )

      Array.from(yAxisGroup.querySelectorAll('.tick')).forEach((tickNode) => {
        const lineNode = ensureGuideLine(tickNode)
        setLineAttributes(lineNode, { x1: 0, y1: 0, x2: lineLength, y2: 0 })
        styleGuideLine(lineNode)
      })
    })
  }
}
